// サーバー管理(設計書 §3.5.4)

import { useState } from 'react';
import { ApiClientError } from '../../api/client';
import {
  SERVER_STATUS_LABELS,
  SLOT_STATE_LABELS,
  useAdminServers,
  useCreateServer,
  useDisableServer,
  useRestartServer,
  type AdminServer,
  type CreateServerResponse,
  type ServerStatus,
  type SlotState,
} from '../../api/admin/servers';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';

const SERVER_NAME_PATTERN = /^[A-Za-z0-9_-]{1,50}$/;

export function AdminServersPage() {
  const serversQuery = useAdminServers();
  const restartMutation = useRestartServer();
  const disableMutation = useDisableServer();
  const [creating, setCreating] = useState(false);
  const [pendingRestartUlid, setPendingRestartUlid] = useState<string | null>(null);
  const [pendingDisable, setPendingDisable] = useState<AdminServer | null>(null);
  const [createdServer, setCreatedServer] = useState<CreateServerResponse | null>(null);

  return (
    <AdminLayout
      title="サーバー"
      description="モザイクサーバーの登録と運用管理。15 秒ごとに更新します。"
      actions={
        <div style={{ minWidth: 160 }}>
          <Button onClick={() => setCreating(true)}>+ サーバー登録</Button>
        </div>
      }
    >
      {serversQuery.isLoading && !serversQuery.data && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>読み込み中…</p>
        </Card>
      )}

      {serversQuery.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {serversQuery.error instanceof ApiClientError
              ? serversQuery.error.message
              : '取得に失敗しました'}
          </p>
        </Card>
      )}

      {serversQuery.data && serversQuery.data.length === 0 && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            まだサーバーが登録されていません。
          </p>
        </Card>
      )}

      {serversQuery.data?.map((server) => (
        <Card key={server.ulid}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 16,
              alignItems: 'flex-start',
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}>{server.name}</h3>
                <StatusBadge status={server.status} />
              </div>
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr',
                  rowGap: 4,
                  margin: '6px 0 12px',
                  fontSize: '11px',
                }}
              >
                <Term>GPU</Term>
                <Detail>{server.gpu_model ?? '—'}</Detail>
                <Term>CPU</Term>
                <Detail>{server.cpu_info ?? '—'}</Detail>
                <Term>エージェント版</Term>
                <Detail>{server.agent_version ?? '—'}</Detail>
                <Term>稼働時間</Term>
                <Detail>{server.uptime_seconds ? formatUptime(server.uptime_seconds) : '—'}</Detail>
                <Term>最終ハートビート</Term>
                <Detail>
                  {server.last_heartbeat_at ? formatDateTime(server.last_heartbeat_at) : '—'}
                </Detail>
              </dl>

              <div style={{ display: 'flex', gap: 8 }}>
                {server.slots.map((slot) => (
                  <div
                    key={slot.slot_name}
                    style={{
                      flex: 1,
                      border: 'var(--border-default)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px 12px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                        Slot {slot.slot_name}
                      </span>
                      <SlotBadge state={slot.state} />
                    </div>
                    {slot.current_session ? (
                      <div style={{ marginTop: 6, fontSize: '11px' }}>
                        <div style={{ color: 'var(--color-text-primary)' }}>
                          {slot.current_session.performer_name}
                        </div>
                        <div style={{ color: 'var(--color-text-tertiary)', fontSize: '10px' }}>
                          開始: {formatDateTime(slot.current_session.started_at)}
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: '10px',
                          color: 'var(--color-text-tertiary)',
                        }}
                      >
                        利用なし
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                minWidth: 130,
              }}
            >
              <Button
                variant="secondary"
                onClick={() => setPendingRestartUlid(server.ulid)}
                style={{ padding: '6px 10px', fontSize: '11px' }}
              >
                再起動
              </Button>
              <Button
                variant="danger"
                onClick={() => setPendingDisable(server)}
                style={{ padding: '6px 10px', fontSize: '11px' }}
              >
                無効化
              </Button>
            </div>
          </div>
        </Card>
      ))}

      {(restartMutation.error || disableMutation.error) && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {extractMessage(restartMutation.error ?? disableMutation.error)}
          </p>
        </Card>
      )}

      <Modal
        open={creating}
        title="サーバーを登録"
        onClose={() => {
          setCreating(false);
          setCreatedServer(null);
        }}
      >
        {createdServer ? (
          <CreatedServerView
            server={createdServer}
            onClose={() => {
              setCreating(false);
              setCreatedServer(null);
            }}
          />
        ) : (
          <CreateServerForm
            onCreated={(s) => setCreatedServer(s)}
            onClose={() => setCreating(false)}
          />
        )}
      </Modal>

      <Modal
        open={pendingRestartUlid !== null}
        title="サーバーを再起動しますか?"
        onClose={() => setPendingRestartUlid(null)}
        width={420}
        footer={
          <>
            <div style={{ minWidth: 100 }}>
              <Button variant="secondary" onClick={() => setPendingRestartUlid(null)}>
                キャンセル
              </Button>
            </div>
            <div style={{ minWidth: 110 }}>
              <Button
                variant="danger"
                disabled={restartMutation.isPending}
                onClick={async () => {
                  if (!pendingRestartUlid) return;
                  try {
                    await restartMutation.mutateAsync(pendingRestartUlid);
                    setPendingRestartUlid(null);
                  } catch {
                    // 親で表示
                  }
                }}
              >
                {restartMutation.isPending ? '実行中…' : '再起動する'}
              </Button>
            </div>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: '12px' }}>
          配信中のセッションが中断される可能性があります。
        </p>
      </Modal>

      <Modal
        open={pendingDisable !== null}
        title="サーバーを無効化しますか?"
        onClose={() => setPendingDisable(null)}
        width={420}
        footer={
          <>
            <div style={{ minWidth: 100 }}>
              <Button variant="secondary" onClick={() => setPendingDisable(null)}>
                キャンセル
              </Button>
            </div>
            <div style={{ minWidth: 110 }}>
              <Button
                variant="danger"
                disabled={disableMutation.isPending}
                onClick={async () => {
                  if (!pendingDisable) return;
                  try {
                    await disableMutation.mutateAsync(pendingDisable.ulid);
                    setPendingDisable(null);
                  } catch {
                    // 親で表示
                  }
                }}
              >
                {disableMutation.isPending ? '実行中…' : '無効化する'}
              </Button>
            </div>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: '12px' }}>
          <strong>{pendingDisable?.name}</strong>{' '}
          を無効化します。新規セッションが割り当てられなくなります。
        </p>
      </Modal>
    </AdminLayout>
  );
}

function CreateServerForm({
  onCreated,
  onClose,
}: {
  onCreated: (server: CreateServerResponse) => void;
  onClose: () => void;
}) {
  const createMutation = useCreateServer();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!SERVER_NAME_PATTERN.test(name)) {
      setError('英数字 / アンダースコア / ハイフンのみ、50 文字以内で入力してください');
      return;
    }
    try {
      const data = await createMutation.mutateAsync(name);
      onCreated(data);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : '登録に失敗しました');
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Input
        label="サーバー名"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="例: mosaic-server-001"
        maxLength={50}
        autoFocus
      />
      <p
        style={{
          margin: '0 0 16px',
          fontSize: '10px',
          color: 'var(--color-text-tertiary)',
        }}
      >
        英数字、アンダースコア、ハイフンのみ。エージェント側 systemd の identifier として使われます。
      </p>
      {error && (
        <p style={{ margin: '0 0 12px', fontSize: '11px', color: 'var(--color-error-text)' }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <div style={{ minWidth: 100 }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
        </div>
        <div style={{ minWidth: 100 }}>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? '登録中…' : '登録'}
          </Button>
        </div>
      </div>
    </form>
  );
}

function CreatedServerView({
  server,
  onClose,
}: {
  server: CreateServerResponse;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(server.api_key);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div>
      <p
        style={{
          margin: '0 0 12px',
          fontSize: '12px',
          color: 'var(--color-warning-text)',
        }}
      >
        この API キーは一度だけ表示されます。エージェント設定にコピーしてください。
      </p>
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 8, fontSize: '11px' }}>
        <Term>サーバー名</Term>
        <Detail>{server.name}</Detail>
        <Term>ULID</Term>
        <Detail style={{ fontFamily: 'monospace' }}>{server.ulid}</Detail>
        <Term>API キー</Term>
        <Detail
          style={{
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            background: 'rgba(0,0,0,0.3)',
            padding: 8,
            borderRadius: 'var(--radius-sm)',
          }}
        >
          {server.api_key}
        </Detail>
      </dl>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <div style={{ minWidth: 130 }}>
          <Button variant="secondary" onClick={copy}>
            {copied ? 'コピーしました' : 'API キーをコピー'}
          </Button>
        </div>
        <div style={{ minWidth: 100 }}>
          <Button onClick={onClose}>閉じる</Button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: ServerStatus }) {
  const colors: Record<ServerStatus, { fg: string; bg: string }> = {
    online: { fg: 'var(--color-success-text)', bg: 'rgba(34,197,94,0.12)' },
    offline: { fg: 'var(--color-text-tertiary)', bg: 'rgba(255,255,255,0.04)' },
    error: { fg: 'var(--color-error-text)', bg: 'rgba(239,68,68,0.12)' },
    maintenance: { fg: 'var(--color-warning-text)', bg: 'rgba(251,191,36,0.1)' },
  };
  const c = colors[status];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 500,
        color: c.fg,
        background: c.bg,
        padding: '2px 8px',
        borderRadius: '3px',
      }}
    >
      {SERVER_STATUS_LABELS[status]}
    </span>
  );
}

function SlotBadge({ state }: { state: SlotState }) {
  const colors: Record<SlotState, { fg: string; bg: string }> = {
    idle: { fg: 'var(--color-text-tertiary)', bg: 'rgba(255,255,255,0.04)' },
    starting: { fg: 'var(--color-warning-text)', bg: 'rgba(251,191,36,0.1)' },
    running: { fg: 'var(--color-success-text)', bg: 'rgba(34,197,94,0.12)' },
    stopping: { fg: 'var(--color-warning-text)', bg: 'rgba(251,191,36,0.1)' },
    error: { fg: 'var(--color-error-text)', bg: 'rgba(239,68,68,0.12)' },
  };
  const c = colors[state];
  return (
    <span
      style={{
        fontSize: '9px',
        fontWeight: 500,
        color: c.fg,
        background: c.bg,
        padding: '2px 6px',
        borderRadius: '3px',
      }}
    >
      {SLOT_STATE_LABELS[state]}
    </span>
  );
}

function Term({ children }: { children: React.ReactNode }) {
  return <dt style={{ color: 'var(--color-text-tertiary)' }}>{children}</dt>;
}

function Detail({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <dd style={{ margin: 0, color: 'var(--color-text-secondary)', ...style }}>{children}</dd>
  );
}

function extractMessage(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  return '処理に失敗しました';
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  if (d > 0) return `${d} 日 ${h} 時間`;
  const m = Math.floor((seconds % 3600) / 60);
  return `${h} 時間 ${m} 分`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'medium' });
}
